/**
 * What Emma asks on each screen, and the offers she makes first
 * (Brian's emma-chat.js v3: CTX and S). The two offers are her first turn in
 * the conversation, the screen's own first.
 */
import { ROUTES } from '@/components/gg/chrome/nav'

export type OfferAsk = 'build' | 'rewrite' | 'adjust' | 'record'
export interface Offer { t: string; s?: string; ask?: OfferAsk; go?: string; pri?: boolean }

export const OFFERS: Record<string, Offer> = {
  build: { t: 'Help me build a story', ask: 'build' },
  rewrite: { t: 'Help me rewrite a script', ask: 'rewrite' },
  adjust: { t: 'Adjust what my feed pulls', ask: 'adjust' },
  record: { t: 'What should I record next?', ask: 'record' },
  topics: { t: 'Tune my topic mix', go: ROUTES.topics },
  places: { t: 'Add or swap a place', go: ROUTES.places },
  watch: { t: 'Watch for breaking news', go: ROUTES.breaking },
  records: { t: 'Find public records', go: ROUTES.recordsSettings },
  plan: { t: 'Explain my seeds and plan', go: `${ROUTES.settings}?tab=plan` },
}

/** Free starters, offered when the week's questions are used. */
export const STARTERS: Offer[] = [
  { t: 'Help me build a story', ask: 'build' },
  { t: 'Help me rewrite a script', ask: 'rewrite' },
  { t: 'Adjust what my brief pulls', ask: 'adjust' },
]

export interface EmmaScreen {
  /** Brian's board for this screen; sent to emma-chat as context. */
  key: string
  /** The screen's name as the person reads it. */
  name: string
  q: string
  offers: string[]
}

const SCREENS: { match: (p: string) => boolean; s: EmmaScreen }[] = [
  { match: (p) => p === ROUTES.feed || p === `${ROUTES.feed}/`, s: { key: 'headlines', name: 'Your feed', q: 'Need help building a story from your feed?', offers: ['build', 'adjust', 'rewrite'] } },
  { match: (p) => p.startsWith('/app/story/'), s: { key: 'article', name: 'A story', q: 'Want help with this story or its script?', offers: ['rewrite', 'build'] } },
  { match: (p) => /^\/app\/alerts\/[^/]+/.test(p), s: { key: 'alert', name: 'An alert', q: 'Want help turning this into a story?', offers: ['build', 'watch'] } },
  { match: (p) => p.startsWith(ROUTES.alerts), s: { key: 'alerts', name: 'Alerts', q: 'Want help with one of these alerts?', offers: ['build', 'watch'] } },
  { match: (p) => p.startsWith(ROUTES.library), s: { key: 'library', name: 'Library', q: 'Need help picking what to record next?', offers: ['record', 'rewrite', 'build'] } },
  { match: (p) => p.startsWith(ROUTES.record), s: { key: 'record', name: 'Record', q: 'Want help with your script before you record?', offers: ['rewrite', 'record'] } },
  { match: (p) => p.startsWith(ROUTES.topics), s: { key: 'settings-topics', name: 'Topics', q: 'Want help tuning your topic mix?', offers: ['adjust', 'topics', 'build'] } },
  { match: (p) => p.startsWith(ROUTES.places), s: { key: 'settings-places', name: 'Places', q: 'Need help choosing places to cover?', offers: ['places', 'adjust', 'records'] } },
  { match: (p) => p.startsWith(ROUTES.breaking), s: { key: 'settings-breaking', name: 'Breaking news', q: 'Want help setting up what to watch?', offers: ['watch', 'adjust'] } },
  { match: (p) => p.startsWith(ROUTES.recordsSettings), s: { key: 'settings-records', name: 'Public records', q: 'Need help with public records for your places?', offers: ['records', 'places', 'adjust'] } },
  { match: (p) => p.startsWith(ROUTES.records), s: { key: 'records', name: 'Record finds', q: 'Want help with one of these record finds?', offers: ['build', 'records'] } },
  { match: (p) => p.startsWith(ROUTES.feedControls), s: { key: 'feed-controls', name: 'Feed controls', q: 'Want help tuning your feed?', offers: ['adjust', 'build'] } },
  // emma-chat.js v3.1 (Oct 29): the desktop's own screens
  { match: (p) => p.startsWith(ROUTES.achievements), s: { key: 'coins', name: 'Achievements', q: 'Want to know how to earn the next one?', offers: ['build', 'record'] } },
  { match: (p) => p.startsWith(ROUTES.newsletterConnect), s: { key: 'settings-newsletter', name: 'Your list', q: 'Questions about your list or sending?', offers: ['plan', 'build'] } },
  { match: (p) => p.startsWith(ROUTES.settings) || p.startsWith(ROUTES.profile), s: { key: 'profile', name: 'Settings', q: 'Questions about your plan or your seeds?', offers: ['plan', 'adjust'] } },
  { match: (p) => p.startsWith(ROUTES.newsletter), s: { key: 'nl-phone', name: 'Newsletter', q: 'Want help with this week’s newsletter?', offers: ['build', 'adjust'] } },
]

const FALLBACK: EmmaScreen = { key: 'other', name: 'GoGrov', q: 'What can I help with?', offers: ['build', 'rewrite', 'adjust'] }

export function emmaScreen(pathname: string): EmmaScreen {
  return SCREENS.find((x) => x.match(pathname))?.s ?? FALLBACK
}

/** Her two offers for the screen: the screen's own first, topped up with the starters. */
export function screenOffers(sc: EmmaScreen): Offer[] {
  const keys = sc.offers.concat(['build', 'adjust', 'rewrite'].filter((k) => !sc.offers.includes(k))).slice(0, 2)
  return keys.map((k, i) => ({ ...OFFERS[k], pri: i === 0 }))
}
